import { useState, useEffect, useRef } from 'react';
import { MessageSquare, Send, Loader2, User, Building2 } from 'lucide-react';

/**
 * Reusable message thread component.
 * Works for both RFI threads (architect ↔ PM) and portal threads (sub ↔ PM).
 *
 * Props:
 * - messages: array of message objects
 * - onSend: async (text) => void — called when user sends a message
 * - senderRole: "pm" | "architect" | "sub" — who the current user is
 * - loading: boolean
 * - placeholder: string
 * - nameField / emailField: for public users (architects/subs) to identify themselves
 * - showIdentity: boolean — whether to show name/email inputs
 * - onNameChange / onEmailChange: for identity fields
 * - name / email: current identity values
 */
export default function MessageThread({
  messages = [],
  onSend,
  senderRole = 'pm',
  loading = false,
  placeholder = 'Type a message...',
  showIdentity = false,
  name = '',
  email = '',
  onNameChange,
  onEmailChange,
}) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const handleSend = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      await onSend(text.trim());
      setText('');
    } catch {}
    setSending(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const roleLabel = (role) => {
    if (role === 'pm') return 'Project Manager';
    if (role === 'architect') return 'Architect';
    if (role === 'sub') return 'Subcontractor';
    return role;
  };

  const roleColor = (role) => {
    if (role === 'pm') return 'bg-blue-500/10 border-blue-500/30 text-blue-400';
    if (role === 'architect') return 'bg-amber-500/10 border-amber-500/30 text-amber-400';
    return 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400';
  };

  const isSelf = (msg) => msg.sender_role === senderRole;

  return (
    <div className="flex flex-col" data-testid="message-thread">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-700/50">
        <MessageSquare className="w-3.5 h-3.5 text-blue-400" />
        <span className="text-[10px] font-heading uppercase tracking-widest text-slate-400">
          Conversation ({messages.length})
        </span>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto max-h-[300px] px-4 py-3 space-y-3" data-testid="message-list">
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-4 h-4 animate-spin text-slate-500" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-6">
            <MessageSquare className="w-6 h-6 text-slate-600 mx-auto mb-2" />
            <p className="text-[11px] text-slate-500">No messages yet. Start the conversation.</p>
          </div>
        ) : (
          messages.map((msg) => (
            <div key={msg.id} className={`flex ${isSelf(msg) ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] ${isSelf(msg) ? 'items-end' : 'items-start'}`}>
                <div className="flex items-center gap-1.5 mb-1">
                  {msg.sender_role === 'pm' ? (
                    <Building2 className="w-3 h-3 text-blue-400" />
                  ) : (
                    <User className="w-3 h-3 text-slate-400" />
                  )}
                  <span className="text-[10px] font-medium text-slate-400">
                    {msg.sender_name || msg.sender_email || roleLabel(msg.sender_role)}
                  </span>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-sm border ${roleColor(msg.sender_role)}`}>
                    {roleLabel(msg.sender_role)}
                  </span>
                </div>
                <div className={`rounded-sm px-3 py-2 text-xs leading-relaxed ${
                  isSelf(msg)
                    ? 'bg-blue-600/20 border border-blue-500/30 text-slate-200'
                    : 'bg-slate-800/50 border border-slate-700/50 text-slate-300'
                }`} data-testid={`message-${msg.id}`}>
                  <p className="whitespace-pre-wrap">{msg.text}</p>
                </div>
                {msg.item_label && (
                  <p className="text-[9px] text-slate-500 mt-0.5 italic">Re: {msg.item_label}</p>
                )}
                <p className="text-[9px] text-slate-600 mt-0.5">
                  {new Date(msg.created_at).toLocaleString()}
                </p>
              </div>
            </div>
          ))
        )}
        <div ref={endRef} />
      </div>

      {/* Identity fields (for public users) */}
      {showIdentity && (
        <div className="flex gap-2 px-4 pt-2">
          <input value={name} onChange={e => onNameChange?.(e.target.value)}
            placeholder="Your name" data-testid="msg-sender-name"
            className="flex-1 px-2.5 py-1.5 text-[11px] bg-slate-800 border border-slate-700 rounded-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-blue-500/50" />
          <input value={email} onChange={e => onEmailChange?.(e.target.value)}
            placeholder="Your email" data-testid="msg-sender-email"
            className="flex-1 px-2.5 py-1.5 text-[11px] bg-slate-800 border border-slate-700 rounded-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-blue-500/50" />
        </div>
      )}

      {/* Input */}
      <div className="flex items-end gap-2 px-4 py-3 border-t border-slate-700/50">
        <textarea value={text} onChange={e => setText(e.target.value)} onKeyDown={handleKeyDown}
          placeholder={placeholder} rows={1} data-testid="msg-input"
          className="flex-1 px-3 py-2 text-xs bg-slate-800 border border-slate-700 rounded-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-blue-500/50 resize-none min-h-[36px] max-h-[100px]"
          style={{ height: Math.min(100, Math.max(36, text.split('\n').length * 20)) + 'px' }} />
        <button onClick={handleSend} disabled={!text.trim() || sending}
          data-testid="msg-send-btn"
          className="flex items-center gap-1 px-3 py-2 text-[10px] font-heading uppercase tracking-wider bg-blue-600 hover:bg-blue-500 text-white rounded-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed shrink-0">
          {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
          Send
        </button>
      </div>
    </div>
  );
}
